import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { getPartsTree } from "../../repositories/SidewallRepository";
import {
  PART_IDS,
  transformAPIToTreeData,
  type PartsTreeRowAPI,
} from "../../models";
/**
 * Query hook for the Sidewall parts tree.
 *
 * Fetches both online and offline sidewalls in parallel,
 * merges and transforms them into SidewallTreeRow[] for the tree panel.
 *
 * Query key: ["sidewall-tree"]
 */
export const useSidewallPartsTreeQuery = () => {
  return useQuery({
    queryKey: ["sidewall-tree"],
    meta: { realtime: ["RBD.EQPGetPartsTree"] },
    refetchInterval: 10_000,
    queryFn: async () => {
      const [onlineResponse, offlineResponse] = await Promise.all([
        getPartsTree(PART_IDS.SIDEWALL),
        getPartsTree(PART_IDS.SPARES),
      ]);

      let allRows: PartsTreeRowAPI[] = [];

      if (onlineResponse.ok) {
        const onlineRows: PartsTreeRowAPI[] = get(
          onlineResponse,
          "data.tables[0].rows",
          [],
        );
        allRows = [...onlineRows];
      }

      if (offlineResponse.ok) {
        const offlineRows: PartsTreeRowAPI[] = get(
          offlineResponse,
          "data.tables[0].rows",
          [],
        );
        allRows = [...allRows, ...offlineRows];
      }

      if (!onlineResponse.ok && !offlineResponse.ok) {
        const errorMsg =
          onlineResponse.message ??
          offlineResponse.message ??
          "Failed to load parts tree";
        throw new Error(errorMsg);
      }

      return transformAPIToTreeData(allRows);
    },
  });
};
